import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { getTsiTeamsAdmin, saveTsiTeamsAdmin } from '../../services/apiService';

// Implicit bucket: everyone not placed in a group. Shown as the left ("Unassigned")
// panel and never stored on the server.
const OTHERS = { id: '__others__', name: 'Others (unassigned)' };

/**
 * TSI Teams admin — group ticket assignees into named, custom teams that drive the
 * Assignee Pivot Table. Dual-list transfer UX: pick a target group, multi-select many
 * people on either side, and move them across in bulk. Add / rename / delete groups.
 */
const TsiTeamsAdmin = () => {
  const [groups, setGroups] = useState([]);        // [{ id, name, members: [] }]
  const [unassigned, setUnassigned] = useState([]); // names in no group
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Editor state
  const [activeGroupId, setActiveGroupId] = useState(OTHERS.id); // right-hand panel target
  const [leftSelected, setLeftSelected] = useState([]);   // selected in Unassigned
  const [rightSelected, setRightSelected] = useState([]); // selected in active group
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [newGroupName, setNewGroupName] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await getTsiTeamsAdmin();
      if (resp.success && resp.data) {
        setGroups((resp.data.groups || []).map((g) => ({ ...g, members: g.members || [] })));
        setUnassigned(resp.data.unassigned || []);
        setDirty(false);
      } else {
        setError(resp.message || 'Failed to load TSI teams');
      }
    } catch (err) {
      setError(err.message || 'Failed to load TSI teams');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeGroup = useMemo(
    () => (activeGroupId === OTHERS.id ? null : groups.find((g) => g.id === activeGroupId) || null),
    [groups, activeGroupId]
  );

  // The members shown on the right panel: the active group's members, or [] for Others
  // (Others is the left panel itself, so there's no right list when it's the target).
  const rightMembers = activeGroup ? activeGroup.members : [];

  const filterNames = (names, q) => {
    const term = q.trim().toLowerCase();
    return term ? names.filter((n) => n.toLowerCase().includes(term)) : names;
  };
  const leftVisible = filterNames(unassigned, leftSearch);
  const rightVisible = filterNames(rightMembers, rightSearch);

  const toggle = (list, setList, name) =>
    setList(list.includes(name) ? list.filter((n) => n !== name) : [...list, name]);

  // Move selected Unassigned → active group.
  const moveToGroup = () => {
    if (!activeGroup || leftSelected.length === 0) return;
    setGroups((prev) => prev.map((g) =>
      g.id === activeGroup.id ? { ...g, members: [...g.members, ...leftSelected] } : g
    ));
    setUnassigned((prev) => prev.filter((n) => !leftSelected.includes(n)));
    setLeftSelected([]);
    setDirty(true);
  };

  // Move selected active-group members → Unassigned.
  const moveToUnassigned = () => {
    if (!activeGroup || rightSelected.length === 0) return;
    setGroups((prev) => prev.map((g) =>
      g.id === activeGroup.id ? { ...g, members: g.members.filter((n) => !rightSelected.includes(n)) } : g
    ));
    setUnassigned((prev) => [...prev, ...rightSelected].sort((a, b) => a.localeCompare(b)));
    setRightSelected([]);
    setDirty(true);
  };

  const addAllVisibleToGroup = () => {
    if (!activeGroup || leftVisible.length === 0) return;
    const names = leftVisible;
    setGroups((prev) => prev.map((g) =>
      g.id === activeGroup.id ? { ...g, members: [...g.members, ...names] } : g
    ));
    setUnassigned((prev) => prev.filter((n) => !names.includes(n)));
    setLeftSelected([]);
    setDirty(true);
  };

  const addGroup = () => {
    const name = newGroupName.trim();
    if (!name) return;
    const id = `g-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}-${groups.length}`;
    setGroups((prev) => [...prev, { id, name, members: [] }]);
    setNewGroupName('');
    setActiveGroupId(id);
    setDirty(true);
  };

  const renameGroup = (id) => {
    const g = groups.find((x) => x.id === id);
    if (!g) return;
    // eslint-disable-next-line no-alert
    const name = window.prompt('Rename group', g.name);
    if (name && name.trim()) {
      setGroups((prev) => prev.map((x) => (x.id === id ? { ...x, name: name.trim() } : x)));
      setDirty(true);
    }
  };

  const deleteGroup = (id) => {
    const g = groups.find((x) => x.id === id);
    if (!g) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete group "${g.name}"? Its ${g.members.length} member(s) move to Unassigned.`)) return;
    setUnassigned((prev) => [...prev, ...g.members].sort((a, b) => a.localeCompare(b)));
    setGroups((prev) => prev.filter((x) => x.id !== id));
    if (activeGroupId === id) setActiveGroupId(OTHERS.id);
    setRightSelected([]);
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const resp = await saveTsiTeamsAdmin(groups.map((g) => ({ id: g.id, name: g.name, members: g.members })));
      if (resp.success) {
        setSuccess('TSI teams saved. The Assignee Pivot Table will use these groups.');
        setDirty(false);
        if (resp.data?.groups) setGroups(resp.data.groups.map((g) => ({ ...g, members: g.members || [] })));
      } else {
        setError(resp.message || 'Failed to save');
      }
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  const NameList = ({ names, selected, onToggle, emptyText }) => (
    <List dense sx={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1, p: 0 }}>
      {names.length === 0 ? (
        <Box sx={{ p: 2 }}><Typography variant="body2" color="textSecondary">{emptyText}</Typography></Box>
      ) : names.map((name) => (
        <ListItemButton key={name} dense onClick={() => onToggle(name)} sx={{ py: 0 }}>
          <Checkbox edge="start" size="small" checked={selected.includes(name)} tabIndex={-1} disableRipple />
          <ListItemText primary={name} primaryTypographyProps={{ variant: 'body2' }} />
        </ListItemButton>
      ))}
    </List>
  );

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <GroupsIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>TSI Teams</Typography>
      </Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Group ticket assignees into teams for the Assignee Pivot Table. Pick a target group,
        select people on the left, and move them across. Anyone not in a group appears under
        “Others”.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Group manager */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1.5}>
          <Typography variant="subtitle2" sx={{ mr: 1 }}>Groups:</Typography>
          {groups.map((g) => (
            <Chip
              key={g.id}
              label={`${g.name} (${g.members.length})`}
              color={activeGroupId === g.id ? 'primary' : 'default'}
              onClick={() => { setActiveGroupId(g.id); setRightSelected([]); }}
              onDelete={() => deleteGroup(g.id)}
              deleteIcon={<Tooltip title="Delete group"><DeleteIcon /></Tooltip>}
              icon={(
                <Tooltip title="Rename group">
                  <EditIcon
                    fontSize="small"
                    onClick={(e) => { e.stopPropagation(); renameGroup(g.id); }}
                    sx={{ cursor: 'pointer' }}
                  />
                </Tooltip>
              )}
            />
          ))}
          <Chip
            label={`${OTHERS.name} (${unassigned.length})`}
            variant="outlined"
            color={activeGroupId === OTHERS.id ? 'primary' : 'default'}
            onClick={() => setActiveGroupId(OTHERS.id)}
          />
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            size="small"
            label="New group name"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addGroup(); }}
          />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addGroup} disabled={!newGroupName.trim()}>
            Add group
          </Button>
        </Box>
      </Paper>

      {/* Transfer editor */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        {activeGroupId === OTHERS.id ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Select a group above (or add one) to move people into it. This list shows the
            currently unassigned people.
          </Alert>
        ) : null}

        <Box display="flex" gap={2} alignItems="stretch" flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
          {/* Left: Unassigned */}
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2">Unassigned ({unassigned.length})</Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="target-group">Move to</InputLabel>
                <Select
                  labelId="target-group"
                  label="Move to"
                  value={activeGroupId === OTHERS.id ? '' : activeGroupId}
                  onChange={(e) => { setActiveGroupId(e.target.value); setRightSelected([]); }}
                >
                  {groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                  {groups.length === 0 && <MenuItem disabled>Add a group first</MenuItem>}
                </Select>
              </FormControl>
            </Box>
            <TextField
              size="small" fullWidth placeholder="Search people…" value={leftSearch}
              onChange={(e) => setLeftSearch(e.target.value)} sx={{ mb: 1 }}
            />
            <NameList names={leftVisible} selected={leftSelected} onToggle={(n) => toggle(leftSelected, setLeftSelected, n)} emptyText="Nobody unassigned." />
            <Box display="flex" gap={1} mt={1}>
              <Button size="small" onClick={() => setLeftSelected(leftVisible)} disabled={!leftVisible.length}>Select all</Button>
              <Button size="small" onClick={() => setLeftSelected([])} disabled={!leftSelected.length}>Clear</Button>
            </Box>
          </Box>

          {/* Middle: transfer buttons */}
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={1} sx={{ minWidth: 120 }}>
            <Tooltip title="Move selected into the group">
              <span>
                <Button variant="contained" size="small" onClick={moveToGroup} disabled={!activeGroup || !leftSelected.length} endIcon={<ArrowForwardIcon />}>
                  Move {leftSelected.length || ''}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Move all currently-shown people into the group">
              <span>
                <Button variant="outlined" size="small" onClick={addAllVisibleToGroup} disabled={!activeGroup || !leftVisible.length} endIcon={<ArrowForwardIcon />}>
                  Move all
                </Button>
              </span>
            </Tooltip>
            <Button variant="outlined" size="small" onClick={moveToUnassigned} disabled={!activeGroup || !rightSelected.length} startIcon={<ArrowBackIcon />}>
              Remove {rightSelected.length || ''}
            </Button>
          </Box>

          {/* Right: active group members */}
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Typography variant="subtitle2" mb={1}>
              {activeGroup ? `${activeGroup.name} (${activeGroup.members.length})` : 'No group selected'}
            </Typography>
            <TextField
              size="small" fullWidth placeholder="Search members…" value={rightSearch}
              onChange={(e) => setRightSearch(e.target.value)} sx={{ mb: 1 }} disabled={!activeGroup}
            />
            <NameList names={rightVisible} selected={rightSelected} onToggle={(n) => toggle(rightSelected, setRightSelected, n)} emptyText={activeGroup ? 'No members yet.' : 'Select a group to see its members.'} />
            <Box display="flex" gap={1} mt={1}>
              <Button size="small" onClick={() => setRightSelected(rightVisible)} disabled={!rightVisible.length}>Select all</Button>
              <Button size="small" onClick={() => setRightSelected([])} disabled={!rightSelected.length}>Clear</Button>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Box display="flex" alignItems="center" gap={2}>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={handleSave} disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="text" onClick={load} disabled={saving}>Reset</Button>
          {dirty && <Typography variant="caption" color="warning.main">Unsaved changes</Typography>}
        </Box>
      </Paper>
    </Box>
  );
};

export default TsiTeamsAdmin;
